import {
  createContext, useCallback, useMemo, useState,
} from 'react';
import Category from '../models/Category/Category';

import categoryAPI from '../services/category';
import { PaginationMetaLink } from '../models/Paginator';

interface CategoryContextProps {
  categories?: Category[],
  total: number
  currentPage?: number
  links?: PaginationMetaLink[]
  requestCategoryList?: (page?: number) => Promise<void>,
  handleSave?: (category: Category) => Promise<void>
  handleDelete?: (id: string) => Promise<void>
  requestAllCategory: () => Promise<Category[]>
  getLastOrderNumber: () => Promise<number>;
}

export const CategoriesContext = createContext<CategoryContextProps>({
  categories: [],
  total: 0,
  requestAllCategory: null,
  getLastOrderNumber: null,
});

interface Props {
  children: JSX.Element
}

const CategoriesProvider = ({ children }: Props) => {
  const [categoryList, setCategoryList] = useState<Category[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [links, setLinks] = useState(null);

  const postOk = useCallback(() => {
    console.log('successfully on creating category');
  }, []);

  const postNg = useCallback((err: any) => {
    console.error('error at create category', err);
  }, []);

  const handleSave = useCallback((category: Category) => {
    if (category.id === '') {
      return categoryAPI.post(category).then(postOk).catch(postNg);
    }
    return categoryAPI.put(category).then(postOk).catch(postNg);
  }, [postOk, postNg]);

  const handleDelete = useCallback((id: string) => categoryAPI.destroy(id).then(postOk).catch(postNg), [postOk, postNg]);

  const requestAllCategory = useCallback(async (): Promise<Category[]> => {
    const result = await categoryAPI.get();
    const cList = result.data.data ?? [];
    return cList;
  }, []);

  const requestCategoryList = useCallback((page?: number) => categoryAPI.get(page || 1).then((res) => {
    if (res == null || res.data == null) {
      return;
    }
    if (!page) page = currentPage;
    const cList = res.data.data ?? [];
    setCategoryList(cList);
    setTotal(res.data.meta.total);
    setLinks(res.data.meta.links);
    setCurrentPage(res.data.meta.current_page);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  const getLastOrderNumber = useCallback(async (): Promise<number> => {
    try {
      const response = await categoryAPI.getLastOrderNumber();
      return response.data;
    } catch (error) {
      console.error('Error getting last order number', error);
      throw error;
    }
  }, []);

  const value = useMemo<CategoryContextProps>(() => ({
    categories: categoryList,
    requestCategoryList,
    handleSave,
    handleDelete,
    total,
    currentPage,
    links,
    requestAllCategory,
    getLastOrderNumber,
  }), [categoryList, requestCategoryList, handleSave, handleDelete, total, currentPage, links, requestAllCategory, getLastOrderNumber]);

  return (
    <CategoriesContext.Provider value={value}>
        {children}
    </CategoriesContext.Provider>
  );
};

export default CategoriesProvider;
